import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder,
  FolderOpen,
  Plus,
  Trash,
  Archive,
  ArrowCounterClockwise,
  PencilSimple,
  FileText,
  UploadSimple,
  DownloadSimple,
  ClockCounterClockwise,
  CheckCircle,
  WarningCircle,
  X,
  CaretRight,
  CaretDown,
  FileCode,
  Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  listProjects,
  getProject,
  createProject,
  updateProject,
  archiveProject,
  restoreProject,
  deleteProject,
  listFiles,
  uploadFileVersion,
  listFileVersions,
  downloadFileVersion,
  listActivities,
  MAX_FILE_SIZE_BYTES
} from '@/api/projects';


const formatFileSize = (bytes) => {
  if (bytes === undefined || bytes === null || isNaN(bytes) || typeof bytes !== 'number' || bytes < 0) {
    return null;
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const ProjectWorkspaceDrawer = ({
  isOpen,
  onClose,
  activeProjectId,
  onSelectProject,
  sessionId
}) => {
  const { isAuthenticated, user, openAuthModal } = useAuth();
  const [activeTab, setActiveTab] = useState('projects'); // 'projects' | 'files' | 'activities'
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Active Project Detail
  const [currentProject, setCurrentProject] = useState(null);
  const [projectDetailLoading, setProjectDetailLoading] = useState(false);

  // Create Project Form State
  const [isCreating, setIsCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Edit Project State
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Delete Confirmation State
  const [deleteConfirmProject, setDeleteConfirmProject] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Files State
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [expandedFileId, setExpandedFileId] = useState(null);
  const [fileVersions, setFileVersions] = useState({});
  const [versionsLoading, setVersionsLoading] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [downloadingVersionId, setDownloadingVersionId] = useState(null);
  const [targetFileForVersion, setTargetFileForVersion] = useState(null);

  // Activities State
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [exportingProject, setExportingProject] = useState(false);
  const lastUserIdRef = React.useRef(user?.user_id);

  const resetAllProjectState = useCallback(() => {
    setCurrentProject(null);
    setProjects([]);
    setFiles([]);
    setFileVersions({});
    setActivities([]);
    setExpandedFileId(null);
    setIsEditing(false);
    setEditName('');
    setEditDesc('');
    setDeleteConfirmProject(null);
    setTargetFileForVersion(null);
    setUploadingFile(false);
    setDownloadingVersionId(null);
    setActiveTab('projects');
  }, []);


  // Fetch projects list (guard for authenticated mode and user isolation)
  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setProjects([]);
      setLoading(false);
      return;
    }
    const capturedUserId = user.user_id;
    try {
      setLoading(true);
      const data = await listProjects();
      if (user?.user_id !== capturedUserId) return; // Discard stale response
      setProjects(data);
    } catch (err) {
      if (user?.user_id !== capturedUserId) return;
      if (err.response?.status === 401) {
        setProjects([]);
      } else {
        toast.error('Error al listar proyectos desde el servidor');
      }
    } finally {
      if (user?.user_id === capturedUserId) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, user]);

  // Fetch active project details with identity and active project matching
  const fetchProjectDetails = useCallback(async (projectId) => {
    if (!projectId || !isAuthenticated || !user) {
      setCurrentProject(null);
      return;
    }
    const capturedUserId = user.user_id;
    const capturedProjectId = projectId;
    try {
      setProjectDetailLoading(true);
      const data = await getProject(projectId);
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      setCurrentProject(data);
      setEditName(data.name || '');
      setEditDesc(data.description || '');
    } catch (err) {
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      if (err.response?.status === 404 || err.response?.status === 410 || err.response?.status === 403) {
        toast.error('El proyecto seleccionado ya no está disponible');
        onSelectProject(null);
      } else {
        toast.error('Error al cargar detalles del proyecto');
      }
    } finally {
      if (user?.user_id === capturedUserId && activeProjectId === capturedProjectId) {
        setProjectDetailLoading(false);
      }
    }
  }, [isAuthenticated, user, activeProjectId, onSelectProject]);

  // Fetch files of active project
  const fetchFiles = useCallback(async (projectId) => {
    if (!projectId || !isAuthenticated || !user) {
      setFiles([]);
      return;
    }
    const capturedUserId = user.user_id;
    const capturedProjectId = projectId;
    try {
      setFilesLoading(true);
      const data = await listFiles(projectId);
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      setFiles(data);
    } catch (err) {
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      toast.error('Error al cargar archivos del proyecto');
    } finally {
      if (user?.user_id === capturedUserId && activeProjectId === capturedProjectId) {
        setFilesLoading(false);
      }
    }
  }, [isAuthenticated, user, activeProjectId]);

  // Fetch activities of active project
  const fetchActivities = useCallback(async (projectId) => {
    if (!projectId || !isAuthenticated || !user) {
      setActivities([]);
      return;
    }
    const capturedUserId = user.user_id;
    const capturedProjectId = projectId;
    try {
      setActivitiesLoading(true);
      const data = await listActivities(projectId);
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      setActivities(data);
    } catch (err) {
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      toast.error('Error al cargar historial de actividad');
    } finally {
      if (user?.user_id === capturedUserId && activeProjectId === capturedProjectId) {
        setActivitiesLoading(false);
      }
    }
  }, [isAuthenticated, user, activeProjectId]);

  // Fetch versions of a file
  const fetchVersions = async (projectId, fileId) => {
    const capturedUserId = user?.user_id;
    const capturedProjectId = projectId;
    try {
      setVersionsLoading(prev => ({ ...prev, [fileId]: true }));
      const data = await listFileVersions(projectId, fileId);
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      setFileVersions(prev => ({ ...prev, [fileId]: data }));
    } catch (err) {
      if (user?.user_id !== capturedUserId || activeProjectId !== capturedProjectId) return;
      toast.error('Error al cargar versiones del archivo');
    } finally {
      if (user?.user_id === capturedUserId && activeProjectId === capturedProjectId) {
        setVersionsLoading(prev => ({ ...prev, [fileId]: false }));
      }
    }
  };

  // Synchronize on identity change, drawer open or project selection change
  useEffect(() => {
    if (!isAuthenticated || !user) {
      resetAllProjectState();
      lastUserIdRef.current = null;
      return;
    }

    if (user.user_id !== lastUserIdRef.current) {
      // Hard identity boundary: new user logged in / identity changed
      lastUserIdRef.current = user.user_id;
      resetAllProjectState();
    }

    if (!activeProjectId) {
      setCurrentProject(null);
      setFiles([]);
      setFileVersions({});
      setActivities([]);
      setExpandedFileId(null);
    }

    if (isOpen && isAuthenticated) {
      fetchProjects();
      if (activeProjectId) {
        fetchProjectDetails(activeProjectId);
        fetchFiles(activeProjectId);
        fetchActivities(activeProjectId);
      }
    }
  }, [isOpen, isAuthenticated, user, activeProjectId, fetchProjects, fetchProjectDetails, fetchFiles, fetchActivities, resetAllProjectState]);

  // Handlers
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) {
      toast.error('El nombre del proyecto es requerido');
      return;
    }
    try {
      setCreateLoading(true);
      const created = await createProject({
        name: newProjectName.trim(),
        description: newProjectDesc.trim(),
        session_id: sessionId
      });
      toast.success(`Proyecto "${created.name}" creado`);
      setNewProjectName('');
      setNewProjectDesc('');
      setIsCreating(false);
      await fetchProjects();
      const newId = created.project_id || created.id;
      if (newId) {
        onSelectProject(newId);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error al crear proyecto';
      toast.error(detail);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!currentProject || !editName.trim()) return;
    const targetId = currentProject.project_id || currentProject.id;
    try {
      setEditLoading(true);
      const updated = await updateProject(targetId, {
        name: editName.trim(),
        description: editDesc.trim()
      });
      toast.success('Proyecto actualizado');
      setCurrentProject(updated);
      setIsEditing(false);
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar proyecto');
    } finally {
      setEditLoading(false);
    }
  };

  const handleExportProject = async (projectId) => {
    try {
      setExportingProject(true);
      toast.info('Preparando exportación ZIP del proyecto...');
      const res = await exportProject(projectId);
      if (res && res.download_url) {
        await downloadProjectExport(res.download_url);
        toast.success('Proyecto exportado en ZIP exitosamente');
      } else {
        throw new Error('No se recibió URL de descarga');
      }
    } catch (err) {
      toast.error('Error al exportar proyecto: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportingProject(false);
    }
  };

  const handleArchiveProject = async (projectId) => {
    try {
      await archiveProject(projectId);
      toast.success('Proyecto archivado');
      await fetchProjects();
      if (activeProjectId === projectId) {
        await fetchProjectDetails(projectId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al archivar proyecto');
    }
  };

  const handleRestoreProject = async (projectId) => {
    try {
      await restoreProject(projectId);
      toast.success('Proyecto restaurado a activo');
      await fetchProjects();
      if (activeProjectId === projectId) {
        await fetchProjectDetails(projectId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restaurar proyecto');
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteConfirmProject) return;
    const targetId = deleteConfirmProject.project_id || deleteConfirmProject.id;
    try {
      setDeleteLoading(true);
      await deleteProject(targetId);
      toast.success(`Proyecto "${deleteConfirmProject.name}" eliminado (Soft-delete)`);
      setDeleteConfirmProject(null);
      if (activeProjectId === targetId) {
        onSelectProject(null);
      }
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar proyecto');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFileUpload = async (event, existingFileName = null) => {
    const file = event.target.files?.[0];
    if (!file || !currentProject) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`El archivo (${(file.size / (1024 * 1024)).toFixed(1)} MB) excede el límite máximo de 50 MB`);
      return;
    }

    try {
      setUploadingFile(true);
      const targetName = existingFileName || file.name;
      let uploadPayloadFile = file;
      if (existingFileName && file.name !== existingFileName) {
        uploadPayloadFile = new File([file], existingFileName, { type: file.type });
      }

      await uploadFileVersion(currentProject.project_id || currentProject.id, uploadPayloadFile);
      toast.success(`Versión subida para "${targetName}"`);
      setTargetFileForVersion(null);
      await fetchFiles(currentProject.project_id || currentProject.id);
      if (expandedFileId) {
        await fetchVersions(currentProject.project_id || currentProject.id, expandedFileId);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error al subir archivo';
      toast.error(detail);
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };


  const handleDownloadVersion = async (file, ver) => {
    const projectId = currentProject?.project_id || currentProject?.id;
    if (!projectId || !file?.file_id || !ver?.version_id) return;

    let blobUrl = null;
    try {
      setDownloadingVersionId(ver.version_id);
      toast.info(`Descargando ${file.filename} (v${ver.version_number})...`);

      const response = await downloadFileVersion(projectId, file.file_id, ver.version_id);
      const blobData = response?.data || response;

      let filename = file.filename || 'download';
      const disposition = response?.headers ? response.headers['content-disposition'] : null;
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const sanitizedFilename = (filename || 'download').split('/').join('_').split('\\').join('_');
      const blob = new Blob([blobData], { type: file.mime_type || 'application/octet-stream' });
      blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', sanitizedFilename);
      document.body.appendChild(link);
      try {
        link.click();
        toast.success(`Descarga completada: ${sanitizedFilename}`);
      } finally {
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    } catch (err) {
      toast.error('Error al descargar versión del archivo: ' + (err.response?.data?.detail || err.message));
    } finally {
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
      setDownloadingVersionId(null);
    }
  };

  const handleToggleExpandFile = (file) => {
    const isExpanding = expandedFileId !== file.file_id;
    setExpandedFileId(isExpanding ? file.file_id : null);
    if (isExpanding && currentProject) {
      fetchVersions(currentProject.project_id || currentProject.id, file.file_id);
    }
  };

  if (!isOpen) return null;

  const filteredProjects = projects.filter(p => showArchived ? p.status === 'archived' : p.status === 'active');

  return (
    <div
      data-testid="project-workspace-drawer"
      className="fixed inset-y-0 left-0 z-50 w-full sm:w-[480px] md:w-[560px] bg-[#0E1017] border-r border-[#1F2937] shadow-2xl flex flex-col transition-all duration-300 font-mono text-xs select-none"
    >
      {/* Drawer Header */}
      <div className="h-14 px-4 bg-[#13131A] border-b border-[#1F2937] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-[#00E5FF] font-bold text-sm tracking-wide terminal-font">
            <FolderOpen size={20} className="text-[#00E5FF]" />
            <span>PROJECT WORKSPACE</span>
          </div>
          {isAuthenticated && user ? (
            <span className="text-[10px] px-1.5 py-0.5 bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 rounded truncate max-w-[150px]">
              {user.display_name || user.email}
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 bg-[#D97706]/20 text-[#D97706] border border-[#D97706]/40 rounded font-semibold">
              ANONYMOUS MODE
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-[#9CA3AF] hover:text-white hover:bg-[#1F2937] rounded transition-colors"
          title="Cerrar Workspace"
        >
          <X size={16} />
        </button>
      </div>

      {/* ANONYMOUS EXPERIENCE (ZERO OPERATIONAL PROJECT CONTROLS EXPOSED) */}
      {!isAuthenticated ? (
        <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center space-y-4 min-h-0">
          <div className="w-14 h-14 rounded-full bg-[#D97706]/10 border border-[#D97706]/30 flex items-center justify-center text-[#D97706] mb-1 shadow-lg">
            <WarningCircle size={32} weight="bold" />
          </div>
          <div className="space-y-1.5 max-w-xs">
            <h3 className="font-bold text-white text-sm tracking-wide uppercase">
              Authentication Required
            </h3>
            <p className="text-[#9CA3AF] text-xs leading-relaxed">
              El espacio de trabajo persistente requiere una cuenta autenticada para garantizar el aislamiento multi-tenant de proyectos, archivos y auditoría.
            </p>
          </div>
          <div className="pt-2 w-full max-w-xs">
            <button
              onClick={openAuthModal}
              data-testid="anonymous-auth-cta"
              className="w-full py-2.5 px-4 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#0A0C10] font-bold rounded shadow-lg transition-all flex items-center justify-center gap-2 text-xs"
            >
              Sign In / Register
            </button>
          </div>
          <div className="p-3 bg-[#13131A] border border-[#1F2937] rounded text-[11px] text-[#6B7280] max-w-xs text-left">
            <span className="font-semibold text-[#9CA3AF] block mb-1">Modo Anónimo Activo</span>
            El Catálogo de Conectores y las Sesiones Demo permanecen totalmente funcionales sin iniciar sesión.
          </div>
        </div>
      ) : (
        /* AUTHENTICATED EXPERIENCE: FULL MISSION 10 PROJECT CONTROLS */
        <>
          {/* Tabs Navigation */}
          <div className="flex items-center border-b border-[#1F2937] bg-[#0A0C10] px-3 gap-1 shrink-0">
            <button
              onClick={() => setActiveTab('projects')}
              className={`py-2.5 px-3 font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === 'projects'
                  ? 'border-[#00E5FF] text-[#00E5FF] bg-[#13131A]/50'
                  : 'border-transparent text-[#9CA3AF] hover:text-white'
              }`}
            >
              <Folder size={14} /> Proyectos ({filteredProjects.length})
            </button>
            <button
              onClick={() => {
                if (!activeProjectId) {
                  toast.info('Selecciona un proyecto primero para gestionar archivos');
                  return;
                }
                setActiveTab('files');
              }}
              disabled={!activeProjectId}
              className={`py-2.5 px-3 font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === 'files'
                  ? 'border-[#00E5FF] text-[#00E5FF] bg-[#13131A]/50'
                  : !activeProjectId
                    ? 'border-transparent text-[#4B5563] cursor-not-allowed'
                    : 'border-transparent text-[#9CA3AF] hover:text-white'
              }`}
            >
              <FileCode size={14} /> Archivos {currentProject ? `(${files.length})` : ''}
            </button>
            <button
              onClick={() => {
                if (!activeProjectId) {
                  toast.info('Selecciona un proyecto primero para ver actividad');
                  return;
                }
                setActiveTab('activities');
              }}
              disabled={!activeProjectId}
              className={`py-2.5 px-3 font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === 'activities'
                  ? 'border-[#00E5FF] text-[#00E5FF] bg-[#13131A]/50'
                  : !activeProjectId
                    ? 'border-transparent text-[#4B5563] cursor-not-allowed'
                    : 'border-transparent text-[#9CA3AF] hover:text-white'
              }`}
            >
              <ClockCounterClockwise size={14} /> Actividad
            </button>
          </div>

          {/* Active Project Banner */}
          <div className="px-4 py-2 bg-[#13131A] border-b border-[#1F2937] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="text-[#9CA3AF] text-[11px]">Activo:</span>
              {currentProject ? (
                <span className="text-[#00E5FF] font-bold truncate">
                  {currentProject.name}
                </span>
              ) : (
                <span className="text-[#6B7280] italic">Ninguno seleccionado</span>
              )}
            </div>
            {currentProject && (
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                  currentProject.status === 'active'
                    ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40'
                    : 'bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/40'
                }`}>
                  {currentProject.status}
                </span>
              </div>
            )}
          </div>

          {/* Main Drawer Content with Scroll */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {/* =================================================== */}
            {/* TAB: PROJECTS */}
            {/* =================================================== */}
            {activeTab === 'projects' && (
              <div className="space-y-4">
                {/* Top Toolbar */}
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => setIsCreating(!isCreating)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#0A0C10] font-bold rounded shadow transition-all"
                  >
                    <Plus size={14} weight="bold" /> Nuevo Proyecto
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowArchived(!showArchived)}
                      className={`px-2.5 py-1 border rounded text-[11px] font-semibold transition-colors flex items-center gap-1 ${
                        showArchived
                          ? 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/40'
                          : 'bg-[#13131A] text-[#9CA3AF] border-[#1F2937] hover:text-white'
                      }`}
                    >
                      <Archive size={12} /> {showArchived ? 'Ver Activos' : 'Ver Archivados'}
                    </button>
                    <button
                      onClick={fetchProjects}
                      disabled={loading}
                      className="p-1.5 text-[#9CA3AF] hover:text-white bg-[#13131A] border border-[#1F2937] rounded"
                      title="Recargar proyectos"
                    >
                      <ArrowCounterClockwise size={14} className={loading ? 'animate-spin' : ''} />
                    </button>
                  </div>
                </div>

                {/* Create Project Form */}
                {isCreating && (
                  <form onSubmit={handleCreateProject} className="p-4 bg-[#13131A] border border-[#00E5FF]/40 rounded space-y-3">
                    <div className="flex items-center justify-between border-b border-[#1F2937] pb-2">
                      <span className="font-bold text-white text-[11px]">CREAR NUEVO PROYECTO PERSISTENTE</span>
                      <button type="button" onClick={() => setIsCreating(false)} className="text-[#9CA3AF] hover:text-white">
                        <X size={14} />
                      </button>
                    </div>
                    <div>
                      <label className="block text-[#9CA3AF] text-[11px] mb-1">Nombre *</label>
                      <input
                        type="text"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder="Ej: VASTEON-M10-E2E"
                        className="w-full bg-[#0A0C10] border border-[#374151] rounded px-3 py-1.5 text-white focus:outline-none focus:border-[#00E5FF]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[#9CA3AF] text-[11px] mb-1">Descripción</label>
                      <textarea
                        value={newProjectDesc}
                        onChange={(e) => setNewProjectDesc(e.target.value)}
                        placeholder="Descripción del proyecto..."
                        className="w-full bg-[#0A0C10] border border-[#374151] rounded px-3 py-1.5 text-white focus:outline-none focus:border-[#00E5FF] h-16 resize-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setIsCreating(false)}
                        className="px-3 py-1 text-[#9CA3AF] hover:text-white"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={createLoading}
                        className="px-4 py-1 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#0A0C10] font-bold rounded disabled:opacity-50"
                      >
                        {createLoading ? 'Creando...' : 'Crear Proyecto'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Edit Project Form */}
                {isEditing && currentProject && (
                  <form onSubmit={handleUpdateProject} className="p-4 bg-[#13131A] border border-[#F59E0B]/40 rounded space-y-3">
                    <div className="flex items-center justify-between border-b border-[#1F2937] pb-2">
                      <span className="font-bold text-white text-[11px]">EDITAR PROYECTO</span>
                      <button type="button" onClick={() => setIsEditing(false)} className="text-[#9CA3AF] hover:text-white">
                        <X size={14} />
                      </button>
                    </div>
                    <div>
                      <label className="block text-[#9CA3AF] text-[11px] mb-1">Nombre *</label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full bg-[#0A0C10] border border-[#374151] rounded px-3 py-1.5 text-white focus:outline-none focus:border-[#F59E0B]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[#9CA3AF] text-[11px] mb-1">Descripción</label>
                      <textarea
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        className="w-full bg-[#0A0C10] border border-[#374151] rounded px-3 py-1.5 text-white focus:outline-none focus:border-[#F59E0B] h-16 resize-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-3 py-1 text-[#9CA3AF] hover:text-white"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={editLoading}
                        className="px-4 py-1 bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0A0C10] font-bold rounded disabled:opacity-50"
                      >
                        {editLoading ? 'Guardando...' : 'Guardar Cambios'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Projects List */}
                {loading ? (
                  <div className="py-8 text-center text-[#9CA3AF]">
                    <ArrowCounterClockwise size={20} className="animate-spin mx-auto mb-2 text-[#00E5FF]" />
                    Cargando proyectos...
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="p-8 text-center border border-dashed border-[#1F2937] rounded text-[#6B7280]">
                    {showArchived ? (
                      'No hay proyectos archivados.'
                    ) : (
                      <div className="space-y-1">
                        <p>No hay proyectos activos.</p>
                        <p className="text-[11px] text-[#4B5563]">Click "+ Nuevo Proyecto" para crear uno.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredProjects.map((p) => {
                      const pId = p.project_id || p.id;
                      const isSelected = activeProjectId === pId;

                      return (
                        <div
                          key={pId}
                          className={`p-3 rounded border transition-all ${
                            isSelected
                              ? 'bg-[#13131A] border-[#00E5FF] shadow-[0_0_10px_rgba(0,229,255,0.1)]'
                              : 'bg-[#0A0C10] border-[#1F2937] hover:border-[#374151]'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div className="flex-1 overflow-hidden">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white truncate text-[13px]">{p.name}</span>
                                {isSelected && (
                                  <span className="px-1.5 py-0.2 bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/40 rounded text-[9px] font-bold">
                                    ACTIVO
                                  </span>
                                )}
                              </div>
                              {p.description && (
                                <p className="text-[#9CA3AF] text-[11px] mt-0.5 line-clamp-2">{p.description}</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-[10px] text-[#6B7280] pt-2 border-t border-[#1F2937]/50 mt-2">
                            <span>Fase: <strong className="text-[#9CA3AF]">{p.current_phase || 'ideation'}</strong></span>
                            <span>{new Date(p.updated_at || p.created_at).toLocaleDateString()}</span>
                          </div>

                          {/* Project Action Buttons */}
                          <div className="flex items-center justify-end gap-1.5 pt-2 mt-1">
                            {!isSelected && p.status === 'active' && (
                              <button
                                onClick={() => onSelectProject(pId)}
                                className="px-2.5 py-1 bg-[#1F2937] hover:bg-[#374151] text-[#00E5FF] rounded font-semibold text-[11px]"
                              >
                                Seleccionar
                              </button>
                            )}

                            {isSelected && (
                              <button
                                onClick={() => {
                                  setIsEditing(true);
                                  setEditName(p.name);
                                  setEditDesc(p.description || '');
                                }}
                                className="p-1.5 text-[#9CA3AF] hover:text-white hover:bg-[#1F2937] rounded"
                                title="Editar Metadatos"
                              >
                                <PencilSimple size={14} />
                              </button>
                            )}

                            {p.status === 'active' ? (
                              <button
                                onClick={() => handleArchiveProject(pId)}
                                className="p-1.5 text-[#9CA3AF] hover:text-[#F59E0B] hover:bg-[#1F2937] rounded"
                                title="Archivar Proyecto"
                              >
                                <Archive size={14} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRestoreProject(pId)}
                                className="p-1.5 text-[#9CA3AF] hover:text-[#10B981] hover:bg-[#1F2937] rounded"
                                title="Restaurar Proyecto"
                              >
                                <ArrowCounterClockwise size={14} />
                              </button>
                            )}

                            <button
                              onClick={() => handleExportProject(pId)}
                              disabled={exportingProject}
                              className="p-1.5 text-[#9CA3AF] hover:text-[#00E5FF] hover:bg-[#1F2937] rounded"
                              title="Exportar Proyecto a ZIP"
                            >
                              <DownloadSimple size={14} />
                            </button>

                            <button
                              onClick={() => setDeleteConfirmProject(p)}
                              className="p-1.5 text-[#9CA3AF] hover:text-[#F85149] hover:bg-[#1F2937] rounded"
                              title="Eliminar (Soft-delete)"
                            >
                              <Trash size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* =================================================== */}
            {/* TAB: FILES */}
            {/* =================================================== */}
            {activeTab === 'files' && (
              !currentProject ? (
                <div className="p-8 text-center border border-dashed border-[#1F2937] rounded text-[#6B7280]" data-testid="no-active-project-files">
                  No hay un proyecto activo seleccionado. Selecciona un proyecto en la pestaña "Proyectos" para ver sus archivos.
                </div>
              ) : (
                <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold text-white text-[11px] uppercase tracking-wider">
                    Archivos del Proyecto ({files.length})
                  </span>

                  <label className={`flex items-center gap-1.5 px-3 py-1.5 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#0A0C10] font-bold rounded shadow transition-all cursor-pointer ${
                    uploadingFile || currentProject?.status === 'archived' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                    <UploadSimple size={14} weight="bold" />
                    <span>{uploadingFile ? 'Subiendo...' : 'Subir Archivo'}</span>
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e)}
                      className="hidden"
                      disabled={uploadingFile || currentProject?.status === 'archived'}
                    />
                  </label>
                </div>

                {filesLoading ? (
                  <div className="py-8 text-center text-[#9CA3AF]">
                    <ArrowCounterClockwise size={20} className="animate-spin mx-auto mb-2 text-[#00E5FF]" />
                    Cargando archivos...
                  </div>
                ) : files.length === 0 ? (
                  <div className="p-8 text-center border border-dashed border-[#1F2937] rounded text-[#6B7280]">
                    <FileText size={24} className="mx-auto mb-2 opacity-50" />
                    <p>No hay archivos en este proyecto.</p>
                    <p className="text-[11px] text-[#4B5563]">Sube un archivo para comenzar el control de versiones.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {files.map((file) => {
                      const isExpanded = expandedFileId === file.file_id;
                      const versions = fileVersions[file.file_id] || [];
                      const isVerLoading = versionsLoading[file.file_id];

                      return (
                        <div
                          key={file.file_id}
                          className="bg-[#13131A] border border-[#1F2937] rounded p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <button
                              onClick={() => handleToggleExpandFile(file)}
                              className="flex items-center gap-2 flex-1 text-left font-bold text-white hover:text-[#00E5FF] truncate"
                            >
                              {isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                              <span className="truncate">{file.filename}</span>
                              <span className="text-[10px] text-[#9CA3AF] font-normal">
                                (v{file.current_version})
                              </span>
                            </button>

                            {formatFileSize(file.size_bytes) && (
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[10px] text-[#6B7280]">
                                  {formatFileSize(file.size_bytes)}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Expanded File Versions Drawer Item */}
                          {isExpanded && (
                            <div className="pl-4 pt-2 border-t border-[#1F2937] space-y-2">
                              <div className="flex items-center justify-between text-[11px] text-[#9CA3AF]">
                                <span className="font-semibold text-white">Historial de Versiones:</span>

                                <label className={`text-[10px] px-2 py-0.5 bg-[#1F2937] hover:bg-[#374151] text-[#00E5FF] rounded cursor-pointer ${
                                  uploadingFile || currentProject?.status === 'archived' ? 'opacity-50 cursor-not-allowed' : ''
                                }`}>
                                  <span>Nueva Versión</span>
                                  <input
                                    type="file"
                                    onChange={(e) => handleFileUpload(e, file.filename)}
                                    className="hidden"
                                    disabled={uploadingFile || currentProject?.status === 'archived'}
                                  />
                                </label>
                              </div>

                              {isVerLoading ? (
                                <div className="py-2 text-center text-[#9CA3AF]">
                                  <ArrowCounterClockwise size={14} className="animate-spin inline mr-1 text-[#00E5FF]" />
                                  Cargando versiones...
                                </div>
                              ) : versions.length === 0 ? (
                                <p className="text-[#6B7280] text-[10px]">No se encontraron versiones registradas.</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {versions.map((ver) => (
                                    <div
                                      key={ver.version_id}
                                      className="p-2 bg-[#0A0C10] border border-[#1F2937] rounded flex items-center justify-between gap-2 text-[11px]"
                                    >
                                      <div className="overflow-hidden flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="font-bold text-[#00E5FF]">v{ver.version_number}</span>
                                          <span className="text-[#6B7280] text-[10px]">
                                            {formatFileSize(ver.size_bytes) ? `(${formatFileSize(ver.size_bytes)})` : ''}
                                          </span>
                                        </div>
                                        <div className="text-[10px] text-[#6B7280] font-mono truncate" title={ver.checksum}>
                                          SHA: {ver.checksum.substring(0, 16)}...
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-[10px] text-[#6B7280]">
                                          {new Date(ver.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <button
                                          onClick={() => handleDownloadVersion(file, ver)}
                                          disabled={downloadingVersionId === ver.version_id}
                                          className="p-1 text-[#00E5FF] hover:bg-[#1F2937] rounded disabled:opacity-50"
                                          title="Descargar esta versión"
                                          data-testid={`btn-download-version-${ver.version_number}`}
                                        >
                                          {downloadingVersionId === ver.version_id ? (
                                            <ArrowCounterClockwise size={14} className="animate-spin" />
                                          ) : (
                                            <DownloadSimple size={14} />
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {/* =================================================== */}
            {/* TAB: ACTIVITIES TIMELINE */}
            {/* =================================================== */}
            {activeTab === 'activities' && (
              !currentProject ? (
                <div className="p-8 text-center border border-dashed border-[#1F2937] rounded text-[#6B7280]" data-testid="no-active-project-activities">
                  No hay un proyecto activo seleccionado. Selecciona un proyecto en la pestaña "Proyectos" para ver su actividad.
                </div>
              ) : (
                <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white text-[11px] uppercase tracking-wider">
                    Auditoría de Actividad (Append-only)
                  </span>
                  <button
                    onClick={() => fetchActivities(currentProject.project_id || currentProject.id)}
                    disabled={activitiesLoading}
                    className="p-1 text-[#9CA3AF] hover:text-white"
                    title="Actualizar actividades"
                  >
                    <ArrowCounterClockwise size={14} className={activitiesLoading ? 'animate-spin' : ''} />
                  </button>
                </div>

                {activitiesLoading ? (
                  <div className="py-6 text-center text-[#9CA3AF]">
                    <ArrowCounterClockwise size={20} className="animate-spin mx-auto mb-2 text-[#00E5FF]" />
                    Cargando timeline...
                  </div>
                ) : activities.length === 0 ? (
                  <div className="p-6 text-center border border-dashed border-[#1F2937] rounded text-[#6B7280]">
                    No hay actividades registradas en este proyecto.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activities.map((act, index) => {
                      return (
                        <div
                          key={act.activity_id || index}
                          className="p-2.5 bg-[#0A0C10] border border-[#1F2937] rounded space-y-1"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-[#00E5FF] text-[11px]">
                              {act.event_type}
                            </span>
                            <span className="text-[10px] text-[#6B7280]">
                              {new Date(act.occurred_at).toLocaleString()}
                            </span>
                          </div>
                          {act.payload && (
                            <div className="p-1.5 bg-[#13131A] rounded text-[10px] text-[#9CA3AF] font-mono overflow-x-auto">
                              {JSON.stringify(act.payload, null, 2)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-xs">
          <div className="w-full max-w-sm rounded border border-[#F85149]/50 bg-[#11131A] p-5 shadow-2xl space-y-4">
            <div className="flex items-center gap-2 text-[#F85149]">
              <WarningCircle size={20} weight="bold" />
              <h3 className="font-bold text-white text-sm">Eliminar Proyecto (Soft-delete)</h3>
            </div>
            <p className="text-[#D1D5DB] text-xs">
              ¿Estás seguro de eliminar el proyecto <strong className="text-white">"{deleteConfirmProject.name}"</strong>?
            </p>
            <p className="text-[#9CA3AF] text-[11px]">
              El proyecto quedará marcado como <code>deleted</code> y no aparecerá en el listado normal.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmProject(null)}
                className="px-3 py-1.5 text-[#9CA3AF] hover:text-white rounded"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteProject}
                disabled={deleteLoading}
                className="px-3 py-1.5 bg-[#F85149] hover:bg-[#F85149]/90 text-white font-bold rounded disabled:opacity-50"
              >
                {deleteLoading ? 'Eliminando...' : 'Confirmar Eliminación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspaceDrawer;
